import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Search, Plus, Pencil, Trash2, Utensils, X } from "lucide-react";
import {
  getMasterLabTests, createMasterLabTest, updateMasterLabTest, deleteMasterLabTest,
  getMasterDiagnosticDepartments,
} from "../../api/api";
import DataTable from "../../components/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { SAMPLE_TYPE_LABEL, formatCurrency } from "../../lib/lab-constants";
import { cn } from "../../lib/utils";

const SAMPLE_TYPES = Object.keys(SAMPLE_TYPE_LABEL);
const EMPTY = {
  code: "", name: "", sample_type: "blood", mrp: "", cost: "",
  category: "General", department_id: "", fasting_required: false, turnaround_hours: 24, active: true,
};

function TestForm({ open, onOpenChange, test, onSaved, categories, departments }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!test;

  useEffect(() => {
    if (!open) return;
    setForm(test ? { ...EMPTY, ...test, department_id: test.department_id?._id || "" } : EMPTY);
  }, [open, test]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Mirrors the server rule, so the desk sees it before a round trip.
  const marginInvalid =
    form.mrp !== "" && form.cost !== "" && Number(form.cost) > Number(form.mrp);

  const submit = async (e) => {
    e.preventDefault();
    if (marginInvalid) return;
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        sample_type: form.sample_type,
        mrp: Number(form.mrp),
        cost: Number(form.cost),
        category: form.category.trim() || "General",
        department_id: form.department_id || null,
        fasting_required: !!form.fasting_required,
        turnaround_hours: Number(form.turnaround_hours) || 24,
        active: !!form.active,
      };
      if (editing) await updateMasterLabTest(test._id, payload);
      else await createMasterLabTest(payload);
      toast.success(editing ? "Test updated." : "Test added to the catalogue.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save the test.");
    } finally {
      setSaving(false);
    }
  };

  const margin = Number(form.mrp || 0) - Number(form.cost || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit test" : "Add test"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="t-code">Code *</Label>
              <Input id="t-code" value={form.code} onChange={(e) => set("code", e.target.value)}
                placeholder="LSL100654" className="font-mono" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-cat">Category</Label>
              <Input id="t-cat" list="lab-categories" value={form.category}
                onChange={(e) => set("category", e.target.value)} placeholder="Haematology" />
              <datalist id="lab-categories">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="t-name">Test name *</Label>
            <Input id="t-name" value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="Absolute Basophil Count" required />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="t-sample">Sample</Label>
              <select id="t-sample" value={form.sample_type} onChange={(e) => set("sample_type", e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                {SAMPLE_TYPES.map((s) => <option key={s} value={s}>{SAMPLE_TYPE_LABEL[s]}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-mrp">MRP (₹) *</Label>
              <Input id="t-mrp" type="number" min={0} value={form.mrp}
                onChange={(e) => set("mrp", e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-cost">Lab cost (₹) *</Label>
              <Input id="t-cost" type="number" min={0} value={form.cost}
                onChange={(e) => set("cost", e.target.value)}
                className={cn(marginInvalid && "border-destructive")} required />
            </div>
          </div>

          {marginInvalid ? (
            <p className="text-xs text-destructive">
              Lab cost cannot exceed MRP — this order would lose money.
            </p>
          ) : form.mrp !== "" && form.cost !== "" ? (
            <p className="text-xs text-muted-foreground">
              Margin {formatCurrency(margin)} ·{" "}
              {Number(form.mrp) > 0 ? Math.round((margin / Number(form.mrp)) * 100) : 0}%
            </p>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="t-dept">Diagnostic department</Label>
              <select id="t-dept" value={form.department_id} onChange={(e) => set("department_id", e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">Not assigned</option>
                {departments.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
              </select>
              <p className="text-xs text-muted-foreground">Its bookings appear on this department's worklist.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-tat">Turnaround (hours)</Label>
              <Input id="t-tat" type="number" min={1} max={720} value={form.turnaround_hours}
                onChange={(e) => set("turnaround_hours", e.target.value)} />
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.fasting_required} onCheckedChange={(v) => set("fasting_required", v)} />
              Requires fasting
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.active} onCheckedChange={(v) => set("active", v)} />
              Available for ordering
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || marginInvalid}>
              {saving ? "Saving…" : editing ? "Save changes" : "Add test"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function LabTestMaster() {
  const [tests, setTests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    getMasterDiagnosticDepartments()
      .then((res) => setDepartments(res.data.filter((d) => d.active)))
      .catch(() => setDepartments([]));
  }, []);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getMasterLabTests({ search: search || undefined, page, limit: 20 })
      .then((res) => {
        setTests(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
        if (res.categories) setCategories(res.categories);
      })
      .catch((err) => toast.error(err?.message || "Could not load the catalogue."))
      .finally(() => setLoading(false));
  }, [search, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (test) => {
    try {
      const res = await deleteMasterLabTest(test._id);
      toast.success(res.message || "Test removed.");
      load();
    } catch (err) {
      toast.error(err?.message || "Could not remove the test.");
    }
  };

  const columns = [
    {
      key: "code",
      header: "Code",
      render: (t) => <span className="font-mono text-xs">{t.code}</span>,
    },
    {
      key: "name",
      header: "Test",
      render: (t) => (
        <div className="min-w-0">
          <p className="truncate font-medium leading-tight">{t.name}</p>
          <p className="flex flex-wrap items-center gap-1.5 text-xs leading-tight text-muted-foreground">
            {t.category}
            {t.department_id?.name && <span>· {t.department_id.name}</span>}
            {t.fasting_required && (
              <Badge variant="warning" className="gap-0.5 px-1.5 py-0 text-[10px] font-normal">
                <Utensils className="h-2.5 w-2.5" /> Fasting
              </Badge>
            )}
          </p>
        </div>
      ),
    },
    {
      key: "sample_type",
      header: "Sample",
      render: (t) => (
        <span className="text-sm text-muted-foreground">
          {SAMPLE_TYPE_LABEL[t.sample_type] || t.sample_type}
        </span>
      ),
    },
    {
      key: "pricing",
      header: "MRP / Cost",
      render: (t) => {
        const margin = (t.mrp || 0) - (t.cost || 0);
        const pct = t.mrp ? Math.round((margin / t.mrp) * 100) : 0;
        return (
          <div className="text-right">
            <p className="text-sm font-medium tabular-nums">{formatCurrency(t.mrp)}</p>
            <p className="text-xs tabular-nums text-muted-foreground">
              cost {formatCurrency(t.cost)} · {pct}%
            </p>
          </div>
        );
      },
    },
    {
      key: "tat",
      header: "TAT",
      render: (t) => <span className="text-sm text-muted-foreground">{t.turnaround_hours}h</span>,
    },
    {
      key: "active",
      header: "Status",
      render: (t) => (
        <Badge variant={t.active ? "secondary" : "outline"}>
          {t.active ? "Active" : "Retired"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (t) => (
        <div className="flex items-center justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8"
            aria-label={`Edit ${t.name}`}
            onClick={() => { setEditing(t); setFormOpen(true); }}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            aria-label={`Remove ${t.name}`}
            onClick={() => remove(t)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Test Catalogue</h2>
          <p className="text-sm text-muted-foreground">
            Every test the care desk can order, with pricing and turnaround
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus className="h-4 w-4" /> Add test
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name, code, or category…"
          className="h-9 pl-9 pr-9"
        />
        {search && (
          <button type="button" onClick={() => setSearch("")} aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {!loading && (
        <p className="text-sm text-muted-foreground">
          {meta.total} test{meta.total === 1 ? "" : "s"}
        </p>
      )}

      <DataTable
        columns={columns}
        data={tests}
        loading={loading}
        emptyMessage={search ? "No tests match that search." : "The catalogue is empty."}
        emptyAction={
          <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4" /> Add the first test
          </Button>
        }
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        rowKey={(t) => t._id}
      />

      <TestForm
        open={formOpen}
        onOpenChange={setFormOpen}
        test={editing}
        categories={categories}
        departments={departments}
        onSaved={load}
      />
    </div>
  );
}
